import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { percent, shortDate } from '@/lib/format';
import { flattenProgress, isBehind, rollupProgress } from '@/lib/progress';
import { useSiteProgress } from '@/lib/queries';
import { useSite } from '@/lib/useSite';
import type { SiteProgress } from '@/lib/types';

/**
 * 공정.
 *
 * <p>맨 위의 <b>전체 공정율</b>이 이 화면의 답이다. 「지금 몇 %인가」를 알려고 들어오는 화면이라
 * 목록보다 그 값이 먼저 커야 한다. 접는 식은 backend 와 같다(`lib/progress.ts`) — 같은 현장인데
 * 내부 화면과 포털의 숫자가 다르면 어느 쪽이 맞는지 아무도 모른다.
 *
 * <p>실적이 계획보다 낮으면 <b>붉게</b> 쓴다. 안전의 「서명 필요」와 같은 약속이다 — 붉은색은
 * 오늘 볼 것이라는 뜻이고, 그러려면 다른 데 쓰지 않아야 한다.
 */
export function ProgressPage() {
  const { siteId, siteName } = useSite();
  const progress = useSiteProgress(siteId);
  const rows = progress.data ?? [];

  const rollup = rollupProgress(rows);
  const nodes = flattenProgress(rows);
  const behind = rollup.actualRate !== null && rollup.plannedRate !== null && rollup.actualRate < rollup.plannedRate;

  return (
    <>
      <PageHeader title="공정" subtitle={siteName} />
      <QueryState
        pending={progress.isPending}
        error={progress.isError}
        empty={rows.length === 0}
        emptyText="등록된 공정이 없습니다."
      >
        <div className="card mb-3">
          <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>전체 공정율</div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span
              className="text-ui-3xl font-semibold"
              style={behind ? { color: 'var(--danger)' } : undefined}
            >
              {percent(rollup.actualRate)}
            </span>
            <span className="text-ui-sm" style={{ color: 'var(--text-secondary)' }}>
              계획 {percent(rollup.plannedRate)}
            </span>
          </div>
          <div className="mt-2">
            <RateBar planned={rollup.plannedRate} actual={rollup.actualRate} />
          </div>
          <div className="mt-2 text-ui-2xs" style={{ color: behind ? 'var(--danger)' : 'var(--text-muted)' }}>
            {rollup.countedItems === 0
              ? '실적이 기록된 공정이 없습니다.'
              : behind
                ? `계획보다 ${gap(rollup.plannedRate, rollup.actualRate)} 뒤져 있습니다 · 지연 공정 ${rollup.behindCount}개`
                : `공정 ${rollup.countedItems}개`}
          </div>
        </div>

        <ul className="flex flex-col gap-2">
          {nodes.map((node) => (
            <ProgressCard key={node.row.id} row={node.row} depth={node.depth} leaf={node.leaf} />
          ))}
        </ul>
      </QueryState>
    </>
  );
}

/** 들여쓰기의 한계. 깊은 나무에서 안쪽 줄이 화면 밖으로 밀리는 것보다 같은 자리에 서는 편이 낫다. */
const MAX_INDENT_DEPTH = 3;

/** 공정 한 줄. 묶음 공정(자식이 있는 줄)은 굵게 세워 나무의 마디가 보이게 한다. */
function ProgressCard({ row, depth, leaf }: { row: SiteProgress; depth: number; leaf: boolean }) {
  const period = planPeriod(row);

  return (
    <li className="card" style={{ marginLeft: Math.min(depth, MAX_INDENT_DEPTH) * 10 }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={`truncate text-ui-md${leaf ? '' : ' font-medium'}`}>{row.name}</div>
          <div className="truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
            {row.code}
            {period !== null && ` · ${period}`}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-ui-lg font-semibold" style={isBehind(row) ? { color: 'var(--danger)' } : undefined}>
            {percent(row.actualRate)}
          </div>
          <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
            계획 {percent(row.plannedRate)}
          </div>
        </div>
      </div>
      <div className="mt-2">
        <RateBar planned={row.plannedRate} actual={row.actualRate} />
      </div>
    </li>
  );
}

/** 계획 기간. 한쪽만 있으면 그 줄을 아예 적지 않는다 — 반쪽 기간은 읽는 사람을 멈추게 한다. */
function planPeriod(row: SiteProgress): string | null {
  if (row.plannedStartDate === null || row.plannedEndDate === null) return null;
  return `${shortDate(row.plannedStartDate)} ~ ${shortDate(row.plannedEndDate)}`;
}

/** 계획 대비 차이. 「몇 %p 뒤졌나」는 백분율의 차이라 단위가 %가 아니다. */
function gap(planned: number | null, actual: number | null): string {
  if (planned === null || actual === null) return '-';
  return `${(planned - actual).toFixed(1)}%p`;
}

/**
 * 계획·실적 막대.
 *
 * <p>막대 둘을 나란히 놓지 않고 <b>계획을 눈금으로</b> 세운다 — 물어보는 것이 「얼마나 했나」가
 * 아니라 「계획만큼 했나」라, 두 값의 자리가 겹쳐 보여야 한 눈에 답이 된다.
 */
function RateBar({ planned, actual }: { planned: number | null; actual: number | null }) {
  const done = clamp(actual);
  const behind = planned !== null && actual !== null && actual < planned;

  return (
    <div
      className="relative overflow-hidden rounded-full"
      style={{ height: 6, background: 'var(--bg-hover)' }}
      aria-hidden
    >
      <div
        className="absolute inset-y-0 left-0 rounded-full"
        style={{ width: `${done}%`, background: behind ? 'var(--danger)' : 'var(--accent)' }}
      />
      {planned !== null && (
        <div
          className="absolute inset-y-0"
          style={{ left: `${clamp(planned)}%`, width: 2, background: 'var(--text-secondary)' }}
        />
      )}
    </div>
  );
}

function clamp(value: number | null): number {
  if (value === null) return 0;
  return Math.max(0, Math.min(100, value));
}
